import { Badge, Flex, Table, Text, Tooltip } from "@radix-ui/themes";
import { type FC } from "react";
import { useTranslation } from "react-i18next";

import { Expandable } from "../../../../../components/Expandable";
import {
  type EnterpriseMemory,
  type EnterpriseMemoryScope,
} from "../../../../../utils/parsers/knowledge-db";
import { memoryTypeLabel } from "../memoryTypes";

interface MemoriesTableProps {
  data: EnterpriseMemory[];
}

const SCOPE_DIMENSIONS: { key: keyof EnterpriseMemoryScope; label: string }[] =
  [
    { key: "brand", label: "Brand" },
    { key: "region", label: "Region" },
    { key: "team", label: "Team" },
    { key: "projectId", label: "Project" },
  ];

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Render the filled-in scope dimensions as labelled badges; a muted dash when
 * none are set (enterprise_id is the tenant itself and is never exposed). */
function MemoryScopeCell({
  id,
  scope,
}: {
  id: string;
  scope: EnterpriseMemoryScope;
}) {
  const filled = SCOPE_DIMENSIONS.map((d) => ({
    ...d,
    value: scope[d.key],
  })).filter((d): d is typeof d & { value: string } => Boolean(d.value));

  if (!filled.length) {
    return (
      <Text color="gray" className="opacity-50">
        -
      </Text>
    );
  }

  return (
    <Flex gap="1" wrap="wrap">
      {filled.map((d) => (
        <Tooltip key={d.key} content={d.label}>
          <Badge
            color="gray"
            variant="outline"
            aria-label={`${d.label}: ${d.value}`}
            data-testid={`memory-scope-${id}-${d.key}`}
          >
            {d.value}
          </Badge>
        </Tooltip>
      ))}
    </Flex>
  );
}

/**
 * Read-only table of enterprise memories. Columns: Type (friendly badge),
 * Scope (filled dimensions), Memory (content), Context (expandable), Confirmed
 * (confirmation count), Updated. No row actions — memories are AI-authored and
 * cannot be edited from the product.
 */
export const MemoriesTable: FC<MemoriesTableProps> = ({ data }) => {
  const { t } = useTranslation("ui");
  return (
    <Table.Root
      variant="surface"
      aria-label={t("settings.memoriesTable.memories")}
      data-testid="memories-table"
    >
      <Table.Header data-testid="memories-table-header">
        <Table.Row>
          <Table.ColumnHeaderCell className="w-40">
            {t("settings.memoriesTable.type")}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className="w-40">
            {t("settings.memoriesTable.scope")}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>
            {t("settings.memoriesTable.memory")}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>
            {t("settings.memoriesTable.context")}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className="w-24">
            <Tooltip content="How many times this memory has been confirmed">
              <span>{t("settings.memoriesTable.confirmed")}</span>
            </Tooltip>
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className="w-32">
            {t("settings.memoriesTable.updated")}
          </Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body data-testid="memories-table-body">
        {!data.length && (
          <Table.Row data-testid="memories-empty-row">
            <Table.Cell colSpan={6} className="text-center">
              <Text color="gray">
                <em>
                  No memories yet — these are generated automatically as your
                  team works in Puntt.
                </em>
              </Text>
            </Table.Cell>
          </Table.Row>
        )}

        {data.map((memory) => (
          <Table.Row
            key={`memory-${memory.id}`}
            data-testid={`memory-row-${memory.id}`}
          >
            <Table.Cell>
              <Badge
                color="gray"
                variant="soft"
                data-testid={`memory-type-${memory.id}`}
              >
                {memoryTypeLabel(memory.type)}
              </Badge>
            </Table.Cell>

            <Table.Cell data-testid={`memory-scope-${memory.id}`}>
              <MemoryScopeCell id={memory.id} scope={memory.scope} />
            </Table.Cell>

            <Table.Cell className="max-w-[40vw] break-words">
              <Expandable
                size="2"
                maxLines={3}
                allowCollapse
                testId={`memory-content-${memory.id}`}
              >
                {memory.content}
              </Expandable>
            </Table.Cell>

            <Table.Cell className="max-w-[40vw] break-words">
              {memory.context ? (
                <Expandable
                  size="2"
                  maxLines={3}
                  allowCollapse
                  testId={`memory-context-${memory.id}`}
                >
                  {memory.context}
                </Expandable>
              ) : (
                <Text color="gray" className="opacity-50">
                  -
                </Text>
              )}
            </Table.Cell>

            <Table.Cell
              className="whitespace-nowrap"
              data-testid={`memory-confirmations-${memory.id}`}
            >
              {memory.confirmationCount > 0 ? (
                <Text
                  aria-label={`Confirmed ${memory.confirmationCount} times`}
                >
                  {memory.confirmationCount}×
                </Text>
              ) : (
                <Text color="gray" className="opacity-50">
                  -
                </Text>
              )}
            </Table.Cell>

            <Table.Cell
              className="whitespace-nowrap"
              data-testid={`memory-updated-${memory.id}`}
            >
              {formatDate(memory.updatedAt)}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
};
